import React, { useState, useRef, useEffect } from 'react';
import type { PartnerSearchResult } from '@sulis-liiga/common';
import teamService from '../../services/teamService';

interface PartnerSearchFieldProps {
  format?: string;
  value: PartnerSearchResult | null;
  onChange: (partner: PartnerSearchResult | null) => void;
  disabled?: boolean;
}

const PartnerSearchField: React.FC<PartnerSearchFieldProps> = ({
  format,
  value,
  onChange,
  disabled = false,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PartnerSearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const q = e.target.value;
    setQuery(q);
    // Clear the selection if the user clears the field
    if (!q) {
      onChange(null);
      setResults([]);
      setOpen(false);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await teamService.searchPartners(q, format);
        setResults(res);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const handleSelect = (partner: PartnerSearchResult) => {
    onChange(partner);
    setQuery(`${partner.firstName} ${partner.lastName}`);
    setOpen(false);
    setResults([]);
  };

  const displayValue = value ? `${value.firstName} ${value.lastName}` : query;

  return (
    <div ref={containerRef} className="relative">
      <label htmlFor="partner-search" className="block text-sm font-medium text-gray-700">
        Partner
      </label>
      <input
        id="partner-search"
        type="text"
        role="combobox"
        autoComplete="off"
        disabled={disabled}
        value={value ? `${value.firstName} ${value.lastName}` : query}
        onChange={value ? undefined : handleInputChange}
        onFocus={() => {
          if (!value && results.length > 0) setOpen(true);
        }}
        readOnly={!!value}
        placeholder="Type a name to search..."
        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:opacity-50"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls="partner-search-results"
      />
      {value && (
        <button
          type="button"
          onClick={() => {
            onChange(null);
            setQuery('');
          }}
          className="absolute right-2 top-8 text-gray-400 hover:text-gray-600"
          aria-label="Clear partner selection"
        >
          &times;
        </button>
      )}
      {searching && <p className="mt-1 text-xs text-gray-400">Searching...</p>}
      {open && results.length > 0 && (
        <ul
          id="partner-search-results"
          role="listbox"
          className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg max-h-48 overflow-auto"
        >
          {results.map(p => (
            <li
              key={p.id}
              role="option"
              aria-selected={false}
              onClick={() => handleSelect(p)}
              className="cursor-pointer px-4 py-2 text-sm hover:bg-primary-50"
            >
              {p.firstName} {p.lastName}
            </li>
          ))}
        </ul>
      )}
      {open && !searching && results.length === 0 && query.length >= 2 && (
        <p className="mt-1 text-xs text-gray-400">No players found.</p>
      )}
      {/* Expose display value for test accessibility — hidden visually */}
      <span className="sr-only" data-testid="partner-display">
        {displayValue}
      </span>
    </div>
  );
};

export default PartnerSearchField;
